/**
 * XRHitTestSystem — Polls hit test results for AR surface placement.
 *
 * Phase: RenderSub, SubPhase: PreUpdate, Priority: 0
 * Active only during AR sessions with 'hit-test' feature granted.
 */

import type { ISystem } from '../../ecs/System.js';
import type { World } from '../../ecs/World.js';
import type { EventBus } from '../../core/EventBus.js';
import type { XRSessionState } from '../types.js';
import { LoopPhase, SubPhase } from '../../shared/types.js';
import { Logger } from '../../core/Logger.js';

export class XRHitTestSystem implements ISystem {
  readonly name = 'XRHitTest';
  readonly phase = LoopPhase.RenderSub;
  readonly subPhase = SubPhase.PreUpdate;
  readonly priority = 0;

  private _state: XRSessionState;
  private _events: EventBus;
  private _hitTestRequested = false;
  private _hitTestSession: XRSession | null = null;
  private _warnedMissingFrame = false;

  constructor(state: XRSessionState, events: EventBus) {
    this._state = state;
    this._events = events;
  }

  execute(_dt: number, _world: World): void {
    if (!this._state.isPresenting) return;
    if (!this._state.hasFeature('hit-test')) return;

    const session = this._state.session!;

    // Reset per-session flag when session changes
    if (session !== this._hitTestSession) {
      this._hitTestRequested = false;
      this._hitTestSession = session;
    }

    // Request hit test source once per session
    if (!this._hitTestRequested && !this._state.hitTestSource) {
      this._hitTestRequested = true;
      this._requestHitTestSource(session).catch((err) => {
        Logger.warn('XR', `Failed to request hit test source: ${err}`);
      });
      return;
    }

    if (!this._state.hitTestSource) return;

    // Get the current XR frame from the session
    // Note: We need the frame from Three.js's XR manager since we're in the rAF callback
    const frame = (globalThis as any).__xrFrame as XRFrame | undefined;
    if (!frame) {
      // Fallback: try reading from renderer.xr.getFrame() in the calling context
      this._state.hasHitResult = false;
      if (!this._warnedMissingFrame) {
        Logger.warn('XRHitTest', '__xrFrame missing during active session — App animation loop not populating globalThis.__xrFrame');
        this._warnedMissingFrame = true;
      }
      return;
    }

    this._pollHitTest(frame);
  }

  private async _requestHitTestSource(session: XRSession): Promise<void> {
    try {
      const viewerSpace = await session.requestReferenceSpace('viewer');
      // Session-identity guard: if the session ended (or was replaced) while
      // requestReferenceSpace was in flight, don't proceed — the source
      // would be bound to a now-defunct session and silently leak.
      if (session !== this._state.session) return;
      const source = await (session as any).requestHitTestSource({
        space: viewerSpace,
        entityTypes: ['plane', 'mesh'],
      }) as XRHitTestSource | undefined;
      // Re-check after the second await — same race window. If the session
      // ended between the two awaits, cancel the orphaned source rather
      // than writing it back into the freshly-reset state.
      if (session !== this._state.session) {
        try { source?.cancel(); } catch { /* ignore */ }
        return;
      }
      this._state.hitTestSource = source ?? null;
      Logger.debug('XR', 'Hit test source created');
    } catch (err) {
      Logger.warn('XR', `Hit test source request failed: ${err}`);
      this._hitTestRequested = false;
    }
  }

  private _pollHitTest(frame: XRFrame): void {
    const hitTestSource = this._state.hitTestSource!;
    const refSpace = this._state.referenceSpace;
    if (!refSpace) return;

    let hasResult = false;

    try {
      const results = frame.getHitTestResults(hitTestSource);
      if (results.length > 0) {
        const hitPose = results[0].getPose(refSpace);
        if (hitPose) {
          // Store the pose matrix for consumers (reticle, placement, etc.)
          if (!this._state.lastHitPoseMatrix) {
            this._state.lastHitPoseMatrix = new Float32Array(16);
          }
          this._state.lastHitPoseMatrix.set(hitPose.transform.matrix);
          hasResult = true;
        }
      }
    } catch {
      // getHitTestResults can throw if frame is inactive
    }

    const prev = this._state.hasHitResult;
    this._state.hasHitResult = hasResult;

    // Only emit when state changes to avoid per-frame event spam
    if (hasResult !== prev) {
      this._events.emit('xr:hit-test-result', { hasResult });
    }
  }

  dispose(): void {
    if (this._state.hitTestSource) {
      this._state.hitTestSource.cancel();
      this._state.hitTestSource = null;
    }
    this._hitTestRequested = false;
    this._hitTestSession = null;
  }
}
